import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';

export type LeadSource = components['schemas']['LeadSourceResponse'];
export type LeadSourceReport = components['schemas']['LeadSourceReportResponse'];

/** Thrown for the 400 the API returns for a label another active source already holds. */
export class DuplicateLeadSourceLabelError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DuplicateLeadSourceLabelError';
  }
}

const CATALOG_KEY = ['lead-sources'] as const;

/**
 * The account's lead-source catalog (M1-28). `customers.read`-gated, which is
 * the difference from M1-38's skill catalog: this vocabulary is about where
 * customers come from, so a Dispatcher — who books the calls it is captured
 * on — holds it.
 *
 * `includeRetired` is for the management screen only; every picker wants
 * active entries, which is why that is the default on the API side rather than
 * something each caller has to remember to ask for.
 */
export function useLeadSources(includeRetired = false, enabled = true) {
  return useQuery({
    queryKey: [...CATALOG_KEY, { includeRetired }] as const,
    enabled,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/lead-sources', {
        params: { query: { includeRetired } },
      });
      if (!response.ok || !data) {
        throw new Error(`GET /lead-sources failed: ${response.status}`);
      }
      return data;
    },
    retry: false,
  });
}

function invalidateCatalog(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: CATALOG_KEY });
  // A rename changes the label every CUSTOMER and JOB response carries, since
  // both denormalize it onto the row for the same reason M1-38's assignments
  // do. A catalog edit that only refreshed the catalog would leave a stale
  // label on a customer record nobody thought to reload.
  void queryClient.invalidateQueries({ queryKey: ['customers'] });
  void queryClient.invalidateQueries({ queryKey: ['jobs'] });
}

/**
 * The server's own wording for a taken label, so the office reads the rule the
 * API actually applied rather than a client-side paraphrase that can drift.
 *
 * ⚠️ Read from openapi-fetch's `error`, NOT by re-reading the `Response` —
 * openapi-fetch has already consumed the body, so `response.clone().json()`
 * throws and silently yields the fallback. Same lesson `useSkills` records.
 */
function duplicateLabelMessage(error: unknown, fallback: string): string {
  const problem = error as { errors?: Record<string, string[]> } | undefined;
  return problem?.errors?.['Label']?.[0] ?? fallback;
}

export function useCreateLeadSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (label: string) => {
      const { data, error, response } = await apiClient.POST('/api/v1/lead-sources', {
        body: { label },
      });
      if (response.status === 400) {
        throw new DuplicateLeadSourceLabelError(
          duplicateLabelMessage(error, 'That lead source could not be added.'),
        );
      }
      if (!response.ok || !data) {
        throw new Error(`POST /lead-sources failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => invalidateCatalog(queryClient),
  });
}

export function useUpdateLeadSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      label,
      sortOrder,
    }: {
      id: string;
      label: string;
      sortOrder: number;
    }) => {
      const { data, error, response } = await apiClient.PUT('/api/v1/lead-sources/{id}', {
        params: { path: { id } },
        body: { label, sortOrder },
      });
      if (response.status === 400) {
        throw new DuplicateLeadSourceLabelError(
          duplicateLabelMessage(error, 'That lead source could not be renamed.'),
        );
      }
      if (!response.ok || !data) {
        throw new Error(`PUT /lead-sources/{id} failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => invalidateCatalog(queryClient),
  });
}

export function useSetLeadSourceRetired() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, retired }: { id: string; retired: boolean }) => {
      // Two routes, not one with a flag — the API's own shape. ⚠️ Both are
      // bodyless POSTs whose URL also contains `/lead-sources`, so a test stub
      // has to match them BEFORE the generic create route or they get
      // swallowed: the most-specific-first rule web/CONVENTIONS.md records.
      const path = retired
        ? '/api/v1/lead-sources/{id}/retire'
        : '/api/v1/lead-sources/{id}/restore';
      const { data, response } = await apiClient.POST(path, { params: { path: { id } } });
      if (!response.ok || !data) {
        throw new Error(`POST ${path} failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => invalidateCatalog(queryClient),
  });
}

/**
 * Jobs by lead source over a window (M1-28) — `jobs.read.all`-gated, unlike the
 * catalog above, because it counts jobs.
 *
 * ⚠️ `from` and `to` are the account's own calendar dates and the server
 * resolves them on the account's timezone. Pass dates computed with
 * `todayInZone` (M1-09), never `new Date()` in the viewer's zone: a viewer in
 * another timezone asking for "March" would otherwise send a window shifted by
 * their own offset and read somebody else's month.
 */
export function useLeadSourceReport(from: string, to: string, enabled = true) {
  return useQuery({
    queryKey: [...CATALOG_KEY, 'report', { from, to }] as const,
    enabled,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/reports/lead-sources', {
        params: { query: { from, to } },
      });
      if (!response.ok || !data) {
        throw new Error(`GET /reports/lead-sources failed: ${response.status}`);
      }
      return data;
    },
    retry: false,
  });
}
